import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { useLacChatbotAnalyticsQuery } from '../../features/lac/queries/useLacChatbotAnalyticsQuery';
import { useLacPlatformChatAnalyticsQuery } from '../../features/lac/queries/useLacPlatformChatAnalyticsQuery';
import {
  mergeKnowledgeBaseStatsCards,
  type LacKnowledgeBaseStatsCard,
} from '../../features/lac/mergeKnowledgeBaseStatsCards';
import type { LacTenantOverviewDateRange } from '../../types/lac';
import { useI18n } from '../../i18n/useI18n';
import { formatNumber } from '../../utils/formatUtils';
import './LacTenantStatsKbCard.css';

const KNOWLEDGE_BASE_SORT_OPTIONS = [
  'chatbots',
  'chatbotMessages',
  'platformSessions',
  'platformMessages',
] as const;

type KnowledgeBaseSortField = (typeof KNOWLEDGE_BASE_SORT_OPTIONS)[number];

const SORT_FIELD_TO_CARD_KEY: Record<
  KnowledgeBaseSortField,
  keyof Pick<
    LacKnowledgeBaseStatsCard,
    | 'chatbotCount'
    | 'chatbotMessageCount'
    | 'platformSessionCount'
    | 'platformMessageCount'
  >
> = {
  chatbots: 'chatbotCount',
  chatbotMessages: 'chatbotMessageCount',
  platformSessions: 'platformSessionCount',
  platformMessages: 'platformMessageCount',
};

function isKnowledgeBaseSortField(
  value: string,
): value is KnowledgeBaseSortField {
  return (KNOWLEDGE_BASE_SORT_OPTIONS as readonly string[]).includes(value);
}

type LacTenantStatsKnowledgeBasesSectionProps = {
  tenantName: string;
  selectedKnowledgeBaseId: string | null;
  onSelectKnowledgeBaseId: (knowledgeBaseId: string | null) => void;
  dateRange?: LacTenantOverviewDateRange;
};

export function LacTenantStatsKnowledgeBasesSection({
  tenantName,
  selectedKnowledgeBaseId,
  onSelectKnowledgeBaseId,
  dateRange,
}: LacTenantStatsKnowledgeBasesSectionProps) {
  const { locale, translate } = useI18n();
  const [sortField, setSortField] =
    useState<KnowledgeBaseSortField>('chatbotMessages');

  const chatbotQuery = useLacChatbotAnalyticsQuery(tenantName, dateRange, {
    groupByKnowledgeBase: true,
  });
  const platformQuery = useLacPlatformChatAnalyticsQuery(
    tenantName,
    dateRange,
    {
      groupByKnowledgeBase: true,
    },
  );

  const cards = useMemo(() => {
    const merged = mergeKnowledgeBaseStatsCards(
      chatbotQuery.data ?? [],
      platformQuery.data ?? [],
    );
    const sortKey = SORT_FIELD_TO_CARD_KEY[sortField];

    return [...merged].sort((a, b) => {
      const byMetric = b[sortKey] - a[sortKey];
      if (byMetric !== 0) {
        return byMetric;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [chatbotQuery.data, platformQuery.data, sortField]);

  const visibleCards = useMemo(() => {
    if (selectedKnowledgeBaseId == null) {
      return cards;
    }
    return cards.filter(
      (card) => card.knowledgeBaseId === selectedKnowledgeBaseId,
    );
  }, [cards, selectedKnowledgeBaseId]);

  const sortOptions = useMemo(
    () =>
      KNOWLEDGE_BASE_SORT_OPTIONS.map((value) => ({
        value,
        label: translate(`lac.organisations.stats.knowledgeBases.${value}`),
      })),
    [translate],
  );

  const isLoading = chatbotQuery.isLoading || platformQuery.isLoading;
  const isError = chatbotQuery.isError || platformQuery.isError;

  const handleCardClick = (knowledgeBaseId: string) => {
    onSelectKnowledgeBaseId(
      selectedKnowledgeBaseId === knowledgeBaseId ? null : knowledgeBaseId,
    );
  };

  return (
    <section
      className="lac-tenant-stats-section"
      aria-label={translate('lac.organisations.stats.knowledgeBases.legend')}
    >
      <div className="lac-tenant-stats-section__header">
        <h2 className="lac-tenant-overview__title">
          {translate('lac.organisations.stats.knowledgeBases.title')}
        </h2>
        {!isLoading && !isError ? (
          <span className="lac-tenant-stats-section__count">
            {formatNumber(cards.length, locale)}
          </span>
        ) : null}
        {selectedKnowledgeBaseId != null ? (
          <LiksButton
            variant="secondary"
            className="lac-tenant-stats-section__show-all"
            onClick={() => onSelectKnowledgeBaseId(null)}
          >
            {translate('lac.organisations.stats.knowledgeBases.showAll')}
          </LiksButton>
        ) : null}
        <div className="lac-tenant-stats-section__criteria">
          <ArrowUpDown
            className="lac-tenant-stats-section__criteria-icon"
            strokeWidth={2}
            aria-hidden
          />
          <LiksDropdown
            className="lac-tenant-stats-section__criteria-dropdown"
            variant="on-light"
            value={sortField}
            onChange={(value) => {
              if (isKnowledgeBaseSortField(value)) {
                setSortField(value);
              }
            }}
            options={sortOptions}
            aria-label={translate(
              'lac.organisations.stats.knowledgeBases.sortLabel',
            )}
          />
        </div>
      </div>

      {isLoading ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.knowledgeBases.loading')}
        </p>
      ) : null}

      {isError ? (
        <p className="lac-tenant-overview__status" role="alert">
          {translate('lac.organisations.stats.knowledgeBases.loadError')}
        </p>
      ) : null}

      {!isLoading && !isError && cards.length === 0 ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.knowledgeBases.empty')}
        </p>
      ) : null}

      {!isLoading && !isError && visibleCards.length > 0 ? (
        <ul className="lac-tenant-stats-kb__grid">
          {visibleCards.map((card) => (
            <li key={card.knowledgeBaseId}>
              <KnowledgeBaseStatsCard
                card={card}
                isSelected={selectedKnowledgeBaseId === card.knowledgeBaseId}
                locale={locale}
                onSelect={() => handleCardClick(card.knowledgeBaseId)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function KnowledgeBaseStatsCard({
  card,
  isSelected,
  locale,
  onSelect,
}: {
  card: LacKnowledgeBaseStatsCard;
  isSelected: boolean;
  locale: string;
  onSelect: () => void;
}) {
  const { translate } = useI18n();
  const description = card.description.trim();
  const hasDescription = description !== '';

  return (
    <div
      className={[
        'lac-tenant-stats-kb__card',
        isSelected && 'lac-tenant-stats-kb__card--selected',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="lac-tenant-stats-kb__card-main"
        aria-pressed={isSelected}
        onClick={onSelect}
      >
        <span className="lac-tenant-stats-kb__card-title">{card.name}</span>
        <span
          className={[
            'lac-tenant-stats-kb__card-description',
            !hasDescription && 'lac-tenant-stats-kb__card-description--empty',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {hasDescription
            ? description
            : translate('lac.organisations.stats.knowledgeBases.noDescription')}
        </span>
        <dl className="lac-tenant-stats-kb__card-stats">
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate('lac.organisations.stats.knowledgeBases.chatbots')}
            </dt>
            <dd>{formatNumber(card.chatbotCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate(
                'lac.organisations.stats.knowledgeBases.chatbotMessages',
              )}
            </dt>
            <dd>{formatNumber(card.chatbotMessageCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate(
                'lac.organisations.stats.knowledgeBases.platformSessions',
              )}
            </dt>
            <dd>{formatNumber(card.platformSessionCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate(
                'lac.organisations.stats.knowledgeBases.platformMessages',
              )}
            </dt>
            <dd>{formatNumber(card.platformMessageCount, locale)}</dd>
          </div>
        </dl>
      </button>
    </div>
  );
}
